import { Check } from "lucide-react";

/**
 * The "still true / needs redo" answer, as a pair of tick boxes.
 *
 * Shared by the triage dialog (the salmon "!" on a sidebar row) and the niche
 * review screen, because they ask the identical question and the answer has to
 * look identical in both — she moves between the two and a different-looking
 * control reads as a different decision.
 *
 * The words live in a column header rather than on every box. Two labelled
 * buttons per section is ten buttons in a five-section module, which is what made
 * the dialog feel cramped; the answer is the same two words each time, so it gets
 * said once at the top. Tooltips carry the meaning on the boxes themselves.
 *
 * Nothing here writes anything. Both screens collect marks and act on them only
 * when she presses the button at the end — which is the whole reason these are
 * tick boxes and not the immediate-action buttons they replaced.
 */

/** Width of one answer column. Header and boxes share it so they line up. */
export const TICK_COL = 66;

export function TickColumnHead({ label }) {
  return (
    <div style={{
      width: TICK_COL, flexShrink: 0, textAlign: "center",
      fontSize: 10, fontWeight: 600, letterSpacing: 0.2,
      color: "var(--text-muted)",
    }}>
      {label}
    </div>
  );
}

export function TickBox({ on, tone = "keep", label, hint, onClick }) {
  const colour = tone === "keep" ? "var(--accent)" : "var(--danger-text)";
  return (
    <div style={{ width: TICK_COL, display: "flex", justifyContent: "center", flexShrink: 0 }}>
      <button
        onClick={onClick}
        title={hint}
        aria-label={label}
        aria-pressed={on}
        style={{
          width: 19, height: 19, padding: 0, borderRadius: 5,
          border: `1.5px solid ${on ? colour : "var(--border-strong)"}`,
          background: on ? colour : "transparent",
          cursor: "pointer", fontFamily: "inherit",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {on && <Check size={12} color="var(--btn-text)" strokeWidth={3} />}
      </button>
    </div>
  );
}

/** The two hints, in one place — both screens say the same thing. */
export const TICK_HINT = {
  keep: "Keep this exactly as it is — it survived the niche change",
  redo: "Go through this one again with Brill during the rebuild",
};
